import { useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { RouteSalesTargetRoute } from "../../types/route-sales-target.type";
import CodeMultiSelect from "../DemandFactor/CodeMultiSelect";
import { PastePreview, TargetDraft, fillMonth, parseAmount, parsePaste } from "./routeSalesTargetDraft";

interface FillDialogProps {
  routes: RouteSalesTargetRoute[];
  draft: TargetDraft;
  today: string;
  onApply: (draft: TargetDraft) => void;
  onClose: () => void;
}

/** ใส่ค่าเดียวทั้งเดือน: one amount per day on the chosen routes. Mount it only while open. */
export function FillMonthDialog({ routes, draft, today, onApply, onClose }: Readonly<FillDialogProps>) {
  const [routeCodes, setRouteCodes] = useState<string[]>([]);
  const [amountText, setAmountText] = useState("");
  const [skipSundays, setSkipSundays] = useState(true);
  const [skipUnplanned, setSkipUnplanned] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const options = useMemo(
    () => routes.map((route) => ({ code: route.route_code, label: `${route.route_code} ${route.route_name}` })),
    [routes],
  );

  const handleApply = () => {
    const parsed = parseAmount(amountText);
    if (routeCodes.length === 0) return setError("กรุณาเลือกอย่างน้อย 1 สาย");
    if (!parsed.ok) return setError(parsed.message);
    if (parsed.value === null) return setError("กรุณาใส่เป้าต่อวัน");
    const chosen = routes.filter((route) => routeCodes.includes(route.route_code));
    onApply(fillMonth(draft, chosen, parsed.value, { skipSundays, skipUnplanned }, today));
  };
  const handleAmount = (event: React.ChangeEvent<HTMLInputElement>) => setAmountText(event.target.value);
  const handleSundays = (_event: unknown, checked: boolean) => setSkipSundays(checked);
  const handleUnplanned = (_event: unknown, checked: boolean) => setSkipUnplanned(checked);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>ใส่ค่าเดียวทั้งเดือน</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          <CodeMultiSelect label="สาย" options={options} value={routeCodes} onChange={setRouteCodes} />
          <TextField size="small" label="เป้าต่อวัน (บาท)" placeholder="เช่น 18,000" value={amountText} onChange={handleAmount} />
          <FormControlLabel control={<Checkbox checked={skipSundays} onChange={handleSundays} />} label="ข้ามวันอาทิตย์" />
          <FormControlLabel control={<Checkbox checked={skipUnplanned} onChange={handleUnplanned} />} label="ข้ามวันที่ไม่มีแผนวิ่ง (ตาม pathway)" />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>ยกเลิก</Button>
        <Button variant="contained" onClick={handleApply}>ใส่ค่า</Button>
      </DialogActions>
    </Dialog>
  );
}

interface PasteDialogProps {
  routes: RouteSalesTargetRoute[];
  draft: TargetDraft;
  today: string;
  onApply: (draft: TargetDraft) => void;
  onClose: () => void;
}

/** วางจาก Excel: paste (Ctrl+V) into the box, review the preview, then apply to the table. */
export function PasteDialog({ routes, draft, today, onApply, onClose }: Readonly<PasteDialogProps>) {
  const [text, setText] = useState("");
  const preview: PastePreview | null = useMemo(
    () => (text.trim() ? parsePaste(text, draft, routes, today) : null),
    [text, draft, routes, today],
  );
  const handleText = (event: React.ChangeEvent<HTMLTextAreaElement>) => setText(event.target.value);
  const handleApply = () => preview && onApply(preview.draft);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>วางจาก Excel</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          <TextField multiline minRows={8} placeholder="วางข้อมูลที่คัดลอกจาก Excel ที่นี่ (Ctrl+V)" value={text} onChange={handleText} />
          {preview && (
            <Stack gap={1}>
              <Alert severity="info">จะเปลี่ยน {preview.changedCount} ช่อง</Alert>
              {preview.unknownCodes.length > 0 && (
                <Alert severity="warning">รหัสสายที่ไม่รู้จัก (ข้าม): {preview.unknownCodes.join(", ")}</Alert>
              )}
              {preview.pastCellCount > 0 && (
                <Alert severity="warning">ช่องที่เป็นวันที่ผ่านไปแล้ว (ข้าม): {preview.pastCellCount} ช่อง</Alert>
              )}
              {preview.invalidCellCount > 0 && (
                <Alert severity="warning">ช่องที่ไม่ใช่ตัวเลข ≥ 0 (ข้าม): {preview.invalidCellCount} ช่อง</Alert>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>ยกเลิก</Button>
        <Button variant="contained" disabled={!preview || preview.changedCount === 0} onClick={handleApply}>
          ใส่ค่าในตาราง
        </Button>
      </DialogActions>
    </Dialog>
  );
}
